import { useAdminAuth } from "@auth/AuthContext"
import { LanguageSwitcher } from "@components/LanguageSwitcher"
import { PasswordField } from "@components/PasswordField"
import { Button } from "@components/ui/Button"
import { Field, TextInput } from "@components/ui/Field"
import { ForgotPasswordForm } from "@features/account-activation"
import { AlertCircle, ChevronRight, Info } from "lucide-react"
import { useEffect, useRef, useState, type FormEvent } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import { ROUTES } from "@/constants/routes"
import { env } from "@/env"

/**
 * The console's door (PRD-60 chunk A). Same family as the professor SPA's
 * login — a side panel from `lg` up and the form — with two differences the
 * critique earned:
 *
 * - The panel does not sell. Nobody signs up here; a director is already a
 *   customer. It says what the console answers this week (PRD-56), in three
 *   true lines.
 * - In dark mode the panel is a darker step of the same ground, not an
 *   inverted near-white block: one theme per page.
 *
 * The h1 belongs to the form on every width (the old one lived in the panel,
 * hidden below `lg`). Errors name what happened — a wrong password, too
 * many attempts, no connection — and a deep link survives the login.
 */
export function LoginPage() {
  const { t } = useTranslation(["auth", "common", "errors"])
  const { login } = useAdminAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [forgotOpen, setForgotOpen] = useState(searchParams.get("olvide") === "1")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const forgotTrigger = useRef<HTMLButtonElement>(null)
  const sessionEnded = searchParams.get("motivo") === "sesion"

  // Where to land after the login: the deep link ProtectedRoute preserved, or
  // the dashboard.
  const from = (location.state as { from?: string } | null)?.from
  const destination = from && from !== ROUTES.login ? from : ROUTES.overview

  useEffect(() => {
    // The reset page sends people here with the form open; drop the flag so
    // a refresh shows the ordinary login.
    if (searchParams.has("olvide")) {
      searchParams.delete("olvide")
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    try {
      // Real login posts to POST /api/admin/auth/login. When VITE_ADMIN_MOCK
      // is true, `login` instead synthesizes a local session so a developer
      // without a backend can get in (see auth/AuthContext.tsx).
      await login(email, password)
      navigate(destination, { replace: true })
    } catch (err) {
      // `login` rejects with an Error whose message is the backend `detail`
      // code (invalid_credentials, too_many_login_attempts, network_error…);
      // AuthContext already logged it, so map it through the errors namespace.
      const code = err instanceof Error ? err.message : "invalid_credentials"
      setError(t([`errors:${code}`, "errors:invalid_credentials"]))
    } finally {
      setLoading(false)
    }
  }

  const closeForgot = () => {
    setForgotOpen(false)
    // Return focus to the control that opened the form.
    requestAnimationFrame(() => forgotTrigger.current?.focus())
  }

  const points = [t("aside.points.averages"), t("aside.points.snapshot"), t("aside.points.at_risk")]

  return (
    <div className="grid min-h-dvh grid-cols-1 lg:grid-cols-2">
      {/* Light: ink on the left, surface on the right. Dark: the inset step
          and the surface — the SPA inverts to near-white here, which breaks
          the one-theme-per-page rule; this is the one deliberate `dark:`
          composition in the console, not a palette step. */}
      <aside className="bg-ink text-ink-inverted dark:bg-inset dark:text-ink dark:border-hairline relative hidden flex-col justify-between p-10 lg:flex xl:p-14 dark:border-r">
        <p className="text-base font-medium tracking-tight">{t("common:app.name")}</p>

        <div className="max-w-xl">
          <p className="text-3xl leading-tight font-bold tracking-tight text-balance xl:text-4xl">
            {t("aside.title")}
          </p>
          <ul className="mt-8 space-y-4">
            {points.map((point) => (
              <li key={point} className="flex items-center gap-3">
                <span className="bg-ink-inverted/10 ring-ink-inverted/15 dark:bg-accent-soft dark:text-accent-ink dark:ring-accent-soft inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1">
                  <ChevronRight className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                </span>
                <span className="text-base">{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-ink-inverted/60 dark:text-ink-subtle text-xs">{t("aside.audience")}</p>
      </aside>

      <main className="bg-surface relative flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        <div className="w-full max-w-sm">
          <p className="text-meta mb-8 lg:hidden">{t("common:app.name")}</p>

          {forgotOpen ? (
            <ForgotPasswordForm initialEmail={email} onBack={closeForgot} />
          ) : (
            <>
              <h1 className="text-ink text-3xl font-bold tracking-tight">{t("login.title")}</h1>
              <p className="text-ink-muted mt-2 text-sm">{t("login.subtitle")}</p>

              {sessionEnded && (
                <p
                  role="status"
                  className="rounded-control bg-inset text-ink-muted mt-4 flex items-start gap-2 px-3 py-2 text-sm"
                >
                  <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  {t("login.session_ended")}
                </p>
              )}

              {env.VITE_ADMIN_MOCK && (
                <p
                  role="note"
                  className="rounded-control border-attention bg-attention-soft text-attention-ink mt-4 border px-3 py-2 text-xs"
                >
                  {t("login.mock_hint")}
                </p>
              )}

              <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
                <Field label={t("login.email.label")}>
                  {({ id, invalid }) => (
                    <TextInput
                      id={id}
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("login.email.placeholder")}
                      aria-invalid={invalid || (error ? true : undefined)}
                      aria-describedby={error ? "login-error" : undefined}
                    />
                  )}
                </Field>

                <PasswordField
                  label={t("login.password.label")}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={error ? true : undefined}
                  describedBy={error ? "login-error" : undefined}
                />

                {error && (
                  <p
                    id="login-error"
                    role="alert"
                    className="rounded-control border-danger bg-danger-soft text-danger-ink flex items-start gap-2 border px-3 py-2 text-sm"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{error}</span>
                  </p>
                )}

                <Button type="submit" size="lg" className="w-full" loading={loading}>
                  {loading ? t("login.submitting") : t("login.submit")}
                </Button>
              </form>

              <div className="border-hairline mt-6 border-t pt-4">
                <Button
                  ref={forgotTrigger}
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setError("")
                    setForgotOpen(true)
                  }}
                >
                  {t("forgot.trigger")}
                </Button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
